<template>
  <div>
    <validation-observer ref="form" novalidate>
      <section-title
        container-class="tag-badge-main-info mt-4 mb-4"
        content="II. Historial Psicosocial"
        icon="fas fa-clipboard-list-check"
      />
      <div class="row">
        <div class="col-12">
          El historial psicosocial surge de:
        </div>

        <div class="col-12 row px-3 py-4">
          <div
            v-for="item in psychosocialHistoryOrigins"
            :key="item?.id"
            class="col-12 col-md-2"
          >
            <check-box
              class="font-weight-bold"
              :class="isReadOnly ? 'disabled-check' : ''"
              :disabled="isReadOnly"
              :text="item.name"
              :value="Boolean(item.checked)"
              @input="togglePsychosocialOrigin(item?.id)"
            />
          </div>
          <div class="col-12">
            <validation-provider
              v-slot="{ errors }"
              :rules="
                psychosocialHistoryOrigins.some((x) => x.checked)
                  ? ''
                  : 'required'
              "
            >
              <error
                v-if="errors[0]"
                class="my-3"
                message="Debe seleccionar al menos una opción."
                variant="no-border"
              />
            </validation-provider>
          </div>
        </div>

        <div class="col-12">
          <validation-provider
            v-if="!isReadOnly"
            v-slot="{ errors }"
            rules="required"
          >
            <custom-text-area
              v-model="model.personalContextInfo"
              :count-classes="['d-block', 'small-counter', 'text-right']"
              custom-error-msg="El campo es requerido."
              :disabled="isReadOnly"
              :error="errors[0]"
              label="Edad, donde vive, con quién y cómo se relaciona con su familia:"
              :maxlength="5000"
              :show-char-count="!isReadOnly"
            />
          </validation-provider>
          <div v-else class="d-flex flex-column">
            <label class="text-muted" style="font-size: 14px">
              Edad, donde vive, con quién y cómo se relaciona con su familia:
            </label>
            <span>{{ model.personalContextInfo }}</span>
          </div>
        </div>
        <div class="col-12">
          <label class="text-muted" style="font-size: 14px">
            Grado que cursa, modalidad, servicios que recibe, funcionamiento
            académico:
          </label>
          <validation-provider
            v-if="!isReadOnly"
            v-slot="{ errors }"
            rules="required"
          >
            <custom-text-area
              v-model="model.academicInfo"
              :count-classes="['d-block', 'small-counter', 'text-right']"
              custom-error-msg="El campo es requerido."
              :disabled="isReadOnly"
              :error="errors[0]"
              :maxlength="5000"
              :show-char-count="!isReadOnly"
            />
          </validation-provider>
          <div v-else class="d-flex flex-column">
            <span>{{ model.academicInfo }}</span>
          </div>
        </div>
        <div class="col-12">
          <label class="text-muted" style="font-size: 14px">
            Historial de salud, diagnósticos principales:
          </label>
          <validation-provider
            v-if="!isReadOnly"
            v-slot="{ errors }"
            rules="required"
          >
            <custom-text-area
              v-model="model.healthHistory"
              :count-classes="['d-block', 'small-counter', 'text-right']"
              custom-error-msg="El campo es requerido."
              :disabled="isReadOnly"
              :error="errors[0]"
              :maxlength="5000"
              :show-char-count="!isReadOnly"
            />
          </validation-provider>
          <div v-else class="d-flex flex-column">
            <label class="text-muted" style="font-size: 14px" />
            <span>{{ model.healthHistory }}</span>
          </div>
        </div>
        <div class="col-12">
          <label class="text-muted" style="font-size: 14px">
            Experiencia laboral, voluntariado:
          </label>
          <validation-provider
            v-if="!isReadOnly"
            v-slot="{ errors }"
            rules="required"
          >
            <custom-text-area
              v-model="model.workAndVolunteerExperience"
              :count-classes="['d-block', 'small-counter', 'text-right']"
              custom-error-msg="El campo es requerido."
              :disabled="isReadOnly"
              :error="errors[0]"
              :maxlength="5000"
              :show-char-count="!isReadOnly"
            />
          </validation-provider>
          <div v-else class="d-flex flex-column">
            <span>{{ model.workAndVolunteerExperience }}</span>
          </div>
        </div>
        <div class="col-12">
          <label class="text-muted" style="font-size: 14px">
            Otra información relevante:
          </label>
          <validation-provider
            v-if="!isReadOnly"
            v-slot="{ errors }"
            rules="required"
          >
            <custom-text-area
              v-if="!isReadOnly"
              v-model="model.additionalRelevantInfo"
              :count-classes="['d-block', 'small-counter', 'text-right']"
              custom-error-msg="El campo es requerido."
              :disabled="isReadOnly"
              :error="errors[0]"
              :maxlength="5000"
              :show-char-count="!isReadOnly"
            />
          </validation-provider>
          <div v-else class="d-flex flex-column">
            <span>{{ model.additionalRelevantInfo }}</span>
          </div>
        </div>
      </div>
    </validation-observer>
  </div>
</template>

<script>
//mixins

//utils
import { copy } from '@/utils/objUtils';

//filters

//components
import CustomTextArea from 'CustomTextArea';
import SectionTitle from '@/shared/components/SectionTitle.vue';
import Error from '@/components/common/Error';
//local-components

//services
import { getTabTwo } from '@/modules/vocational-evaluation/api.js';

//constants
import vocationalEvaluationOptionTypes from '@/utils/constants/vocationalEvaluationOptionTypes';

export default {
  name: 'VocationalEducationPartTwo',
  components: {
    CustomTextArea,
    SectionTitle,
    Error,
  },
  mixins: [],
  props: {
    id: {
      type: [Number, String],
      default: () => null,
    },
    appointmentId: {
      type: [Number, String],
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    value: {
      type: Object,
      required: true,
    },
    initialModel: {
      type: Object,
      required: false,
      default: () => null,
    },
    keepUpdates: {
      type: Boolean,
      required: false,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    filled() {
      return this.initialModel?.filled ?? false;
    },
    allOptions() {
      return this.model?.options || [];
    },
    psychosocialHistoryOrigins() {
      return this.allOptions.filter(
        (x) =>
          x?.type?.id ===
          vocationalEvaluationOptionTypes.OrigenHistorialPsicosocial
      );
    },
  },
  async mounted() {
    this.$emit('update:disabled', true);
    await this.get();
    this.$emit('update:disabled', false);
  },
  beforeDestroy() {
    this.$emit('update:initialModel', this.model);
  },
  methods: {
    setModelFrom(value) {
      const data = copy(value);
      this.model = data;
      this.$emit('update:initialModel', data);
    },
    async get() {
      if (this.keepUpdates && this.filled) {
        this.setModelFrom(this.initialModel);
        return;
      }

      if (!this.appointmentId) return;

      const { data } = await getTabTwo({
        id: this.id,
        appointmentId: this.appointmentId,
      });

      const result = {
        ...data,
        filled: true,
      };

      this.setModelFrom(result);
    },
    async validate() {
      return await this.$refs.form.validate();
    },
    togglePsychosocialOrigin(id) {
      if (this.isReadOnly) return;

      const key = 'options';
      const updatedOptions = this.allOptions.map((item) => {
        if (
          item?.type?.id ===
            vocationalEvaluationOptionTypes.OrigenHistorialPsicosocial &&
          item?.id === id
        ) {
          return {
            ...item,
            checked: !item.checked,
          };
        }

        return item;
      });

      this.$set(this.model, key, updatedOptions);
    },
  },
};
</script>
<style scoped lang="scss">
.disabled-check {
  ::v-deep(.option-input:disabled:checked + label span) {
    background-color: #3f5b60 !important;
    border-color: #3f5b60 !important;
  }

  ::v-deep(.option-input:disabled:checked + label span .ic-check) {
    background-image: url('../../../../../assets/img/icon-check-white.svg') !important;
    background-repeat: no-repeat !important;
    background-position: center !important;
    background-size: 12px 12px !important;

    width: 20px !important;
    height: 20px !important;
    margin: 0 !important;
    display: block !important;
  }
  ::v-deep(.bg-white) {
    background-color: #c3c3c3 !important;
    cursor: not-allowed;
    opacity: 1;
  }
}
</style>
